/**
 * Loads everything the drawer matches against.
 *
 * Deliberately unfiltered: the Fleet page scopes its table to the active
 * industry tab, but a pasted manifest can name any unit the admin can see.
 * Matching against the filtered set would report real units as "not matched",
 * so this fetches the caller's whole visible fleet.
 */

import { useCallback, useEffect, useState } from 'react';
import { getAdminUnits, getAdminGroups, getAdminCompanys } from '@/lib/adminApi';
import type { BulkAssignCompany, BulkAssignGroup, BulkAssignUnit } from './types';

interface RawGroup {
  id: string;
  name: string;
  company_id: string;
  parent_group_id: string | null;
}

interface RawUnit {
  id: string;
  unit_code: string;
  model_code?: string | null;
  company_id: string;
  primary_group_id?: string | null;
  status?: string | null;
}

export function useBulkAssignData(open: boolean) {
  const [units, setUnits] = useState<BulkAssignUnit[]>([]);
  const [parentGroups, setParentGroups] = useState<BulkAssignGroup[]>([]);
  const [sites, setSites] = useState<BulkAssignGroup[]>([]);
  const [companies, setCompanies] = useState<BulkAssignCompany[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [unitsRes, groupsRes, companiesRes] = await Promise.all([
        getAdminUnits(),
        getAdminGroups(),
        getAdminCompanys(),
      ]);

      const rawGroups: RawGroup[] = groupsRes.groups ?? [];
      const groupById = new Map(rawGroups.map((g) => [g.id, g]));
      const companyList: BulkAssignCompany[] = (companiesRes.companies ?? []).map(
        (c: { id: string; name: string }) => ({ id: c.id, name: c.name })
      );
      const companyNameById = new Map(companyList.map((c) => [c.id, c.name]));

      const toGroup = (g: RawGroup): BulkAssignGroup => ({
        id: g.id,
        name: g.name,
        companyId: g.company_id,
        parentGroupId: g.parent_group_id ?? null,
      });

      setParentGroups(rawGroups.filter((g) => !g.parent_group_id).map(toGroup));
      setSites(rawGroups.filter((g) => g.parent_group_id).map(toGroup));
      setCompanies(companyList);

      setUnits(
        (unitsRes.units ?? []).map((u: RawUnit): BulkAssignUnit => {
          const directGroup = u.primary_group_id ? groupById.get(u.primary_group_id) : undefined;
          // A unit sits on a site or straight on the production; the site's
          // parent is the production either way.
          const production = directGroup?.parent_group_id
            ? groupById.get(directGroup.parent_group_id)
            : directGroup;

          return {
            id: u.id,
            unitCode: u.unit_code,
            modelCode: u.model_code ?? '',
            companyId: u.company_id,
            companyName: companyNameById.get(u.company_id) ?? '—',
            primaryGroupId: u.primary_group_id ?? null,
            parentGroupId: production?.id ?? null,
            parentGroupName: production?.name ?? null,
            siteId: directGroup?.parent_group_id ? directGroup.id : null,
            status: u.status ?? 'active',
          };
        })
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load fleet data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  return { units, parentGroups, sites, companies, loading, error, reload: load };
}
